import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PulsarService } from "@/services/pulsar.service";
import { CHALLENGES_KEYS } from "@/hooks/challenges";
import { STORAGE_KEYS } from "@/config/constants";
import { usePlausible } from "next-plausible";
import { getErrorMessage } from "@/helpers/getErrorMessage";

export const useActivateReferral = (
  campaignId: string,
  groupId: string | undefined,
  address?: string,
  isAuthenticated: boolean = false
) => {
  const queryClient = useQueryClient();
  const plausible = usePlausible();

  const clearStoredReferralCode = () => {
    localStorage.removeItem(STORAGE_KEYS.referralCode);
  };

  const removeReferralFromUrl = () => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("referralCode");
      const newUrl = url.pathname + url.search;
      window.history.replaceState({}, "", newUrl);
    }
  };

  return useMutation({
    mutationFn: async (referralCode: string) => {
      if (!referralCode.trim()) {
        throw new Error("Please enter a valid referral code");
      }

      await PulsarService.createReferral(campaignId, groupId, referralCode);

      // Track successful activation
      plausible("Referral: Apply", {
        props: { referralCode },
      });

      return referralCode;
    },
    onSuccess: (referralCode) => {
      toast.success("Referral code activated successfully! 🎉");

      // Clear the stored referral code
      clearStoredReferralCode();

      // Remove referral from URL
      removeReferralFromUrl();

      // Invalidate user data to reflect new referral status
      queryClient.invalidateQueries({
        queryKey: CHALLENGES_KEYS.campaignUser(campaignId, groupId || "", address || "", isAuthenticated),
      });
      queryClient.invalidateQueries({
        queryKey: CHALLENGES_KEYS.taskStatus(campaignId, groupId || "", address || "", isAuthenticated),
      });
    },
    onError: (error: any) => {
      const errorMessage = getErrorMessage(error, "Failed to activate referral code");
      toast.error(errorMessage);

      // Track failed activation
      plausible("Referral: Error", {
        props: { error: errorMessage },
      });
    },
  });
};